import { stringToColor } from '@/components/LetterAvatar/LetterAvatarUtils'
import { useAuthorizationFormHook } from '@/lib/AuthorizationForm/hooks/useAuthorizationFormHook'
import { RoleDTO } from '@/services/Hub/Authorization/contract/response/roles.response'
import { EllipsisText } from '@components'
import { IOnSubmissionCall } from '@lib'
import { Article, Delete, Edit, Group, LabelImportant } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  Button
} from '@mui/material'
import { useState, useCallback } from 'react'

import { RoleCopyModal } from './RoleCopyModal'
import { RoleDeleteModal } from './RoleDeleteModal'

interface IRoleCard {
  role: RoleDTO
  deleteRole: (roleId: string) => Promise<void>
  handleClickEditRole: (role: RoleDTO) => void
  organizationId: string
  onSubmissionCall?: IOnSubmissionCall
}

export const RoleCard = (props: IRoleCard) => {
  const { role, deleteRole, handleClickEditRole, organizationId, onSubmissionCall } = props
  const memberQuantity = role.membershipIds?.length || 0

  const [isDeleting, setIsDeleting] = useState(false)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [openCopyModal, setOpenCopyModal] = useState(false)

  const { submitForm } = useAuthorizationFormHook({
    organizationId,
    selectedRole: undefined,
    onSubmissionCall
  })

  const handleOpenConfirmModal = useCallback(() => setOpenConfirmModal(true), [])
  const handleCloseConfirmModal = useCallback(() => setOpenConfirmModal(false), [])

  const handleOpenCopyModal = useCallback(() => setOpenCopyModal(true), [])
  const handleCloseCopyModal = useCallback(() => setOpenCopyModal(false), [])

  const handleDeleteRole = useCallback(async () => {
    setIsDeleting(true)
    handleCloseConfirmModal()
    await deleteRole(role.id)
    setIsDeleting(false)
  }, [deleteRole, role.id, handleCloseConfirmModal])

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '10px',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: '10px',
          borderRadius: '8px',
          width: '100%',

          background: isDeleting ? '#c73636' : '#fff',
          transition: 'all 2s ease-in'
        }}>
        <Box
          sx={{
            minWidth: 'fit-content',
            width: '25%',
            flexShrink: 1,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: '8px'
          }}>
          <Tooltip placement="top" title="Nome do Cargo">
            <Box
              sx={{
                height: '12px',
                width: '12px',
                minWidth: '12px',
                borderRadius: '50%',
                backgroundColor: stringToColor(role.label)
              }}
            />
          </Tooltip>
          <EllipsisText text={role.label} customPlacement="top" maxLength={40} variant="body1" />

          <Typography
            variant="caption"
            sx={{
              color: '#3f6ad6',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '2px',

              '& svg': {
                height: '16px',
                width: '16px'
              }
            }}>
            ({memberQuantity}
            <Group />)
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box
          sx={{
            flexGrow: 1,
            flexShrink: 1,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: '8px'
          }}>
          {role.tag && (
            <>
              <Tooltip placement="top" title="Tag do cargo">
                <LabelImportant color="primary" />
              </Tooltip>
              <Typography variant="body2">{role.tag.name}</Typography>
            </>
          )}
          {role.description && (
            <>
              <Tooltip placement="top" title="Descrição do cargo">
                <Article color="primary" />
              </Tooltip>
              <Typography variant="body2">{role.description}</Typography>
            </>
          )}
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box
          sx={{
            width: 'fit-content',
            maxWidth: 'fit-content',
            minWidth: 'fit-content',
            flexDirection: 'row',
            display: 'flex',
            justifyContent: 'flex-start',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: '8px'
          }}>
          <Button variant="outlined" onClick={handleOpenCopyModal}>
            Duplicar
          </Button>
          <IconButton
            size="small"
            onClick={handleOpenConfirmModal}
            disabled={isDeleting}
            sx={{
              color: '#db4f42',
              '& svg': {
                height: '20px',
                width: '20px'
              }
            }}>
            {isDeleting ? <CircularProgress size={20} color="error" /> : <Delete />}
          </IconButton>
          <IconButton
            size="small"
            disabled={isDeleting}
            onClick={() => handleClickEditRole(role)}
            sx={{
              '& svg': {
                height: '20px',
                width: '20px'
              }
            }}>
            <Edit />
          </IconButton>
        </Box>
      </Paper>

      <RoleDeleteModal
        open={openConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={handleDeleteRole}
        itemName={role.label}
        memberCount={memberQuantity}
      />

      <RoleCopyModal
        open={openCopyModal}
        onClose={handleCloseCopyModal}
        role={role}
        submitForm={submitForm}
      />
    </>
  )
}
